import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  forwardRef,
  useRef,
} from "react";
import { Input } from "@UI/Components/Input/Input";
import { Box } from "@UI/Components/Box/Box";
import { FormLabel } from "@UI/Components/Form/Form";
import useI18n from "@I18n/hooks/useI18n";
import messages from "../../../messages";
import { QuestionFormControl } from "../QuestionFormControl/QuestionFormControl";
import { BaseQuestionProps } from "../types";

export interface DateQuestionProps extends BaseQuestionProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  /** "pill" is the compact borderless chip used inside an option card's row. */
  variant?: "default" | "pill";
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
}

/** Chip styling for the "pill" variant — see the Availability screen's 4th option card. */
const pillProps = {
  bg: "rgba(118, 118, 128, 0.12)",
  color: "#08F",
  fontWeight: "medium",
  fontSize: "sm",
  borderRadius: "full",
  paddingX: "10px",
  height: "36px",
  cursor: "pointer",
} as const;

/**
 * The design shows the date alone, so the chip draws the value itself and the
 * native field covers it invisibly — keeping `showPicker()`, the native picker
 * and keyboard focus. Hiding the calendar glyph in CSS instead only works in
 * Blink/WebKit (`::-webkit-calendar-picker-indicator`): Gecko draws its own
 * button in the UA shadow DOM and no selector reaches it.
 */
const overlayProps = {
  position: "absolute",
  top: "0",
  left: "0",
  width: "100%",
  height: "100%",
  opacity: 0,
  cursor: "pointer",
} as const;

/**
 * A `YYYY-MM-DD` answer read as local time: `new Date("2026-09-15")` is UTC
 * midnight, which formats as the day before for anyone west of UTC.
 */
const toLocalDate = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);

  return new Date(year, month - 1, day);
};

/**
 * Keeps the pill unwritable from the keyboard while leaving the input mutable,
 * so `showPicker()` still works. Tab/Enter/Escape stay live: they are how the
 * field is reached and how the picker is opened and dismissed.
 */
const preventTyping = (event: KeyboardEvent<HTMLInputElement>) => {
  if (!["Tab", "Enter", "Escape"].includes(event.key)) event.preventDefault();
};

/**
 * Native date input for now — no calendar-picker library exists in this codebase yet;
 * introducing one is a build-vs-buy call to make separately if a richer UX is needed.
 */
// ponytail: the chip formats the date itself; a custom picker only if design insists
export const DateQuestion = forwardRef<HTMLInputElement, DateQuestionProps>(
  (
    {
      label,
      value,
      onChange,
      min,
      max,
      variant = "default",
      onFocus,
      onClick,
      isRequired = false,
      isInvalid = false,
      errorMessage = "",
      isDisabled = false,
    },
    forwardedRef
  ) => {
    const { formatDate, formatMessage } = useI18n();
    const inputRef = useRef<HTMLInputElement>(null);

    // The parent (SingleSelectQuestion's option card) needs this same node to
    // open the picker on a card-wide click, not just the input's own click
    // below — so the internal ref and the forwarded one both get it.
    const setRefs = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    };

    // The invisible field gives the user nothing to click to open the picker, so
    // open it ourselves. Guarded: showPicker is Chrome 99+/Safari 16+ and throws
    // outside a user gesture — older browsers keep the keyboard-editable input.
    const handleClick = (event: MouseEvent<HTMLInputElement>) => {
      onClick?.(event);
      if (typeof inputRef.current?.showPicker === "function") {
        inputRef.current.showPicker();
      }
    };

    const input = (
      <Input
        type="date"
        value={value}
        onChange={handleChange}
        onFocus={onFocus}
        onClick={handleClick}
        ref={setRefs}
        min={min}
        max={max}
        isDisabled={isDisabled}
        // Pill is a display-only chip: the date can only be set through the
        // native picker, never typed. Done by swallowing keystrokes rather than
        // `isReadOnly`, because `showPicker()` throws `InvalidStateError` on a
        // readonly input — which killed the picker on every click.
        onKeyDown={variant === "pill" ? preventTyping : undefined}
        {...(variant === "pill" ? overlayProps : {})}
      />
    );

    // The pill lives inside another question's option card, which already owns the
    // label. Its error renders right below it in that same card instead of here —
    // see `SingleSelectQuestion`'s `trailing`/`trailingError` handling.
    if (variant === "pill")
      return (
        <Box
          position="relative"
          display="inline-flex"
          alignItems="center"
          opacity={isDisabled ? 0.5 : 1}
          _focusWithin={{ boxShadow: `0 0 0 2px ${pillProps.color}` }}
          {...pillProps}
        >
          {value
            ? formatDate(toLocalDate(value), {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : formatMessage(messages["taskCreation.date.placeholder"])}
          {input}
        </Box>
      );

    return (
      <QuestionFormControl
        isRequired={isRequired}
        isInvalid={isInvalid}
        errorMessage={errorMessage}
        isDisabled={isDisabled}
      >
        {label && <FormLabel>{label}</FormLabel>}
        {input}
      </QuestionFormControl>
    );
  }
);

DateQuestion.displayName = "DateQuestion";
